/**
 * Derived journey stages for a set of bookings, for any surface that shows a
 * tenant status outside the tenant profile.
 *
 * The stage is derived from EVIDENCE (deposit paid in full, key pickup
 * recorded, protocol submitted, refund settled), so a surface cannot render it
 * from the booking row alone — it has to load the same rows the profile loads.
 * Every surface that skipped that step fell back to `bookings.status` and
 * disagreed with the profile; the room view's hardcoded "Active" badge was the
 * last one. This hook loads exactly the four narrow things the gates need and
 * hands back the resolved journey per booking id.
 *
 * @see src/lib/tenantJourneySignals.ts — the shared signal assembly.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { isMissingTable } from "@/hooks/v2/useDepositSettlement";
import { fetchAllByColumn } from "@/hooks/v2/usePropertyRentPaidStats";
import { isMissingColumn } from "@/hooks/v2/useContractFlowReady";
import { isSupersededByReopen } from "@/lib/protocol";
import {
  bookingWasRealTenancy,
  resolveTenantJourneyFromSources,
  type DepositSettlementLite,
} from "@/lib/tenantJourneySignals";
import type { TenantJourney } from "@/lib/tenantJourney";
import type { QueryPollOptions } from "@/lib/query-poll";

type JourneyBooking = { id: number } & Record<string, any>;

/**
 * A submitted protocol reduced to what "does this still stand?" needs.
 * `submitted_at` alone is not enough: an admin recording backdates it, so
 * `isSupersededByReopen` ranks by `recorded_at` (the real filing moment) and
 * `auto_submitted_at` as well.
 */
type SubmittedProtocolLite = {
  kind: string;
  status: "submitted";
  submitted_at: string | null;
  recorded_at: string | null;
  auto_submitted_at: string | null;
};

// Module-level defaults: an inline `= {}` is a new object every render, which
// rebuilds `journeys` every render while a query is disabled or loading.
const NO_ROWS: any[] = [];
const NO_PROOFS: Record<string, any> = {};
const NO_PROTOCOLS = new Map<number, SubmittedProtocolLite[]>();
const NO_SETTLEMENTS = new Map<number, DepositSettlementLite>();

/** Stable cache key: the ids themselves, order-independent. */
function idsKey(ids: number[]): string {
  return [...ids].sort((a, b) => a - b).join(",");
}

export interface TenantJourneysResult {
  journeys: Map<number, TenantJourney>;
  /**
   * Per booking id: did it become a real tenancy — move-in recorded or deposit
   * paid (see `bookingWasRealTenancy`)? Only meaningful once `isLoading` is
   * false and `depositEvidenceFailed` is false.
   */
  realTenancy: Map<number, boolean>;
  /**
   * The deposit rows (or their proofs) could not be read on the initial load.
   * An absent row reads as "nothing paid", so a caller that FILTERS on
   * `realTenancy` must check this first rather than silently hide everyone. A
   * failed background refetch does not count: the cached rows are still good.
   */
  depositEvidenceFailed: boolean;
  /**
   * The deposit rows or their proofs — the only inputs of `realTenancy` — have
   * not arrived yet. Narrower than `isLoading`, which also waits on protocols
   * and settlements that don't affect it. Counts a query as loading while it is
   * enabled and still pending, NOT only while it is fetching: a retry that is
   * paused (hidden tab, offline) is `pending` but not `fetching`, and reading
   * that as "done" would classify everyone from an empty payments list.
   */
  depositEvidenceLoading: boolean;
  /** True while any evidence row is still in flight — see the note above. */
  isLoading: boolean;
  /**
   * True when an evidence query failed. The journeys then fall back to "no
   * evidence" (earlier stages), so a caller that lists stages must not trust them.
   */
  isError: boolean;
  /**
   * A failed query has NO data to fall back on (it never loaded). False when
   * only a background refetch failed: the journeys still read the last rows.
   */
  isLoadingError: boolean;
}

export function useTenantJourneys(
  bookings: readonly JourneyBooking[] | null | undefined,
  poll?: QueryPollOptions
): TenantJourneysResult {
  const ids = useMemo(
    () =>
      (bookings ?? [])
        .map((b) => Number(b?.id))
        .filter((id) => Number.isFinite(id)),
    [bookings]
  );
  const key = useMemo(() => idsKey(ids), [ids]);
  const enabled = ids.length > 0;

  /** Deposit rows + their proofs decide the "deposit paid in full" gate. */
  // Every read below is chunked and paged (fetchAllByColumn): a portfolio-wide
  // id list would otherwise overflow the URL or be cut at PostgREST's row cap.
  const {
    data: payments = NO_ROWS,
    isLoading: paymentsLoading,
    isPending: paymentsPending,
    isError: paymentsError,
    isLoadingError: paymentsFailed,
  } = useQuery<any[]>({
    queryKey: ["v2", "journey", "deposit-payments", key],
    queryFn: () =>
      fetchAllByColumn<any>(
        "payments",
        "id, booking_id, type, status, stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by, amount_cents, due_date, paid_at",
        "booking_id",
        ids,
        "id",
        true,
        (q) => q.eq("type", "deposit")
      ),
    enabled,
    ...poll,
  });

  const paymentIds = useMemo(
    () => payments.map((p: any) => p?.id).filter(Boolean),
    [payments]
  );

  const {
    data: proofByPaymentId = NO_PROOFS,
    isLoading: proofsLoading,
    isPending: proofsPending,
    isError: proofsError,
    isLoadingError: proofsFailed,
  } = useQuery<Record<string, any>>({
    queryKey: ["v2", "journey", "payment-proofs", paymentIds.join(",")],
    queryFn: async () => {
      // A payment's proofs all land in one chunk, newest first.
      const rows = await fetchAllByColumn<any>(
        "payment_proofs",
        "payment_id, status, created_at",
        "payment_id",
        paymentIds,
        "created_at",
        false,
        undefined,
        100 // UUIDs
      );
      const map: Record<string, any> = {};
      for (const row of rows) {
        if (row?.payment_id == null) continue;
        // Newest proof per payment id wins (the order above guarantees it).
        if (map[String(row.payment_id)]) continue;
        map[String(row.payment_id)] = row;
      }
      return map;
    },
    enabled: paymentIds.length > 0,
    ...poll,
  });

  /**
   * Submitted protocols decide the Active / Key return gates.
   *
   * Kept per row, with the filing timestamps, rather than collapsed to a set of
   * kinds: "submitted" is not final. A reopen granted after a record was filed
   * retires it (`isSupersededByReopen`), and the reopen stamp lives on the
   * booking — so the comparison happens in the memo below, where that row is in
   * hand. Without it this hook kept a reopened move-out at "Key return
   * pending" while the profile and the Tenants list had it back at the
   * protocol stage.
   */
  const {
    data: submittedByBookingId = NO_PROTOCOLS,
    isLoading: protocolsLoading,
    isError: protocolsError,
    isLoadingError: protocolsMissing,
  } = useQuery<Map<number, SubmittedProtocolLite[]>>({
    queryKey: ["v2", "journey", "submitted-protocols", key],
    queryFn: async () => {
      const m = new Map<number, SubmittedProtocolLite[]>();
      const run = (cols: string) =>
        fetchAllByColumn<any>(
          "handover_protocols",
          cols,
          "booking_id",
          ids,
          "id",
          true,
          (q) => q.eq("status", "submitted")
        );
      // Step down on a MISSING COLUMN only: an environment a push behind
      // 20260818100000 / 20260820150000 lacks the filing timestamps, and a
      // wholesale 42703 would blank every stage rather than just lose the
      // supersede rule. A real error still throws. fetchAllByColumn keeps
      // PostgREST's message, which isMissingColumn reads.
      let rows: any[];
      try {
        rows = await run(
          "booking_id, kind, submitted_at, recorded_at, auto_submitted_at"
        );
      } catch (error) {
        if (!isMissingColumn(error)) throw error;
        rows = await run("booking_id, kind, submitted_at");
      }
      for (const row of rows) {
        const bid = Number(row?.booking_id);
        if (!Number.isFinite(bid)) continue;
        if (!m.has(bid)) m.set(bid, []);
        m.get(bid)!.push({
          kind: String(row?.kind ?? ""),
          status: "submitted",
          submitted_at: row?.submitted_at ?? null,
          recorded_at: row?.recorded_at ?? null,
          auto_submitted_at: row?.auto_submitted_at ?? null,
        });
      }
      return m;
    },
    enabled,
    ...poll,
  });

  /**
   * Settlements decide the final Moved out gate. A database without the
   * settlement migration yields an empty map rather than throwing — that reads
   * as "not settled", the same conservative default the resolver takes.
   */
  const {
    data: settlementByBookingId = NO_SETTLEMENTS,
    isLoading: settlementsLoading,
    isError: settlementsError,
    isLoadingError: settlementsMissing,
  } = useQuery<Map<number, DepositSettlementLite>>({
    queryKey: ["v2", "journey", "deposit-settlements", key],
    queryFn: async () => {
      const m = new Map<number, DepositSettlementLite>();
      let rows: any[];
      try {
        rows = await fetchAllByColumn<any>(
          "deposit_settlements",
          "booking_id, refund_paid_at, deposit_held_cents, amounts_snapshot_at",
          "booking_id",
          ids,
          "id",
          true
        );
      } catch (error) {
        // fetchAllByColumn keeps PostgREST's message, which isMissingTable reads.
        if (isMissingTable(error)) return m;
        throw error;
      }
      for (const row of rows) {
        const bid = Number(row?.booking_id);
        if (!Number.isFinite(bid)) continue;
        m.set(bid, {
          refund_paid_at: row?.refund_paid_at ?? null,
          deposit_held_cents: row?.deposit_held_cents ?? null,
          amounts_snapshot_at: row?.amounts_snapshot_at ?? null,
        });
      }
      return m;
    },
    enabled,
    ...poll,
  });

  const isLoading =
    paymentsLoading || proofsLoading || protocolsLoading || settlementsLoading;
  const isError =
    paymentsError || proofsError || protocolsError || settlementsError;
  const isLoadingError =
    paymentsFailed || proofsFailed || protocolsMissing || settlementsMissing;

  const { journeys, realTenancy } = useMemo(() => {
    const paymentsByBooking = new Map<number, any[]>();
    for (const p of payments) {
      const bid = Number(p?.booking_id);
      if (!Number.isFinite(bid)) continue;
      if (!paymentsByBooking.has(bid)) paymentsByBooking.set(bid, []);
      paymentsByBooking.get(bid)!.push(p);
    }
    const proofFor = (paymentId: string) =>
      proofByPaymentId[String(paymentId)] ?? null;

    const out = new Map<number, TenantJourney>();
    const tenancy = new Map<number, boolean>();
    for (const booking of bookings ?? []) {
      const bid = Number(booking?.id);
      if (!Number.isFinite(bid)) continue;
      tenancy.set(
        bid,
        bookingWasRealTenancy(booking, {
          payments: paymentsByBooking.get(bid) ?? [],
          proofFor,
        })
      );
      // Only kinds with a record that still stands count as submitted.
      const submittedKinds = new Set<string>();
      for (const p of submittedByBookingId.get(bid) ?? []) {
        const reopenedAt =
          (p.kind === "move_in"
            ? booking?.move_in_protocol_reopened_at
            : booking?.move_out_protocol_reopened_at) ?? null;
        if (!isSupersededByReopen(p, reopenedAt)) submittedKinds.add(p.kind);
      }
      out.set(
        bid,
        resolveTenantJourneyFromSources(booking, {
          payments: paymentsByBooking.get(bid) ?? [],
          proofFor,
          submittedProtocolKinds: submittedKinds,
          settlement: settlementByBookingId.get(bid) ?? null,
        })
      );
    }
    return { journeys: out, realTenancy: tenancy };
  }, [
    bookings,
    payments,
    proofByPaymentId,
    submittedByBookingId,
    settlementByBookingId,
  ]);

  return {
    journeys,
    realTenancy,
    depositEvidenceFailed: paymentsFailed || proofsFailed,
    depositEvidenceLoading:
      (ids.length > 0 && paymentsPending) ||
      (paymentIds.length > 0 && proofsPending),
    isLoading,
    isError,
    isLoadingError,
  };
}

/**
 * Single-booking convenience wrapper. `journey` is null while the evidence is
 * loading, so a caller can render its placeholder on that alone.
 */
export function useTenantJourney(booking: JourneyBooking | null | undefined): {
  journey: TenantJourney | null;
  isLoading: boolean;
} {
  const list = useMemo(() => (booking ? [booking] : []), [booking]);
  const { journeys, isLoading } = useTenantJourneys(list);
  return {
    journey:
      booking && !isLoading ? (journeys.get(Number(booking.id)) ?? null) : null,
    isLoading,
  };
}
